import { useState, useEffect } from "react";
import Skill, { SkillCategory } from "../types/Skill";
import {
  addNewSkill,
  getAllSkills,
  updateSkill as updateSkillAPI,
  deleteSkill as deleteSkillAPI,
} from "../services/definitionsService";

export function useSkills() {
  const [skills, setSkills] = useState<Skill[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSkills = async () => {
    setLoading(true);
    try {
      const skillsData = await getAllSkills();
      setSkills(skillsData);
    } catch (err) {
      setError("Failed to fetch skills.");
    } finally {
      setLoading(false);
    }
  };

  const addSkill = async (title: string, skillCategory: SkillCategory) => {
    const newPos: Skill = {
      skillId: "", // let backend generate ID
      name: title,
      type: skillCategory,
    };

    try {
      const response = await addNewSkill(newPos); // backend creates and returns new skill
      const saved = response.skill;
      const formattedResponse: Skill = {
        skillId: saved.skillId,
        name: saved.name,
        type: saved.type as SkillCategory,
      };
      setSkills((prev) => [...prev, formattedResponse]);
      return formattedResponse;
    } catch (err) {
      console.error("Failed to add skill:", err);
      setError("Failed to add skill.");
      throw err;
    }
  };

  const updateSkill = async (
    id: string,
    title: string,
    skillCategory: SkillCategory
  ) => {
    const updatedPos: Skill = {
      skillId: id,
      name: title,
      type: skillCategory,
    };

    try {
      const response = await updateSkillAPI(updatedPos); // backend updates and returns saved item
      const saved = response.skill;
      const formattedResponse = {
        skillId: saved.skillId,
        name: saved.name,
        type: saved.type,
      };
      setSkills((prev) =>
        prev.map((pos) => (pos.skillId === id ? formattedResponse : pos))
      );
    } catch (err) {
      console.error("Failed to update skill:", err);
      setError("Failed to update skill.");
    }
  };

  const deleteSkill = async (id: string): Promise<boolean> => {
    try {
      await deleteSkillAPI(id);
      setSkills((prev) => prev.filter((pos) => pos.skillId !== id));
      return true;
    } catch (err: any) {
      console.error("Failed to delete skill:", err);
      setError("Failed to delete skill: " + err.response.data.error);
      return false;
    }
  };

  useEffect(() => {
    fetchSkills();
  }, []);

  return {
    skills,
    loading,
    error,
    fetchSkills,
    addSkill,
    updateSkill,
    deleteSkill,
  };
}
